import { Capacitor } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { App } from '@capacitor/app';

/**
 * Screen Wake Lock Utility
 * Keeps the device screen awake during active LiveKit calls (Live Class and AI Tutor)
 * across Android native, iOS native, mobile browsers, and desktop browsers.
 */

const activeLocks = new Set();
let webWakeLockSentinel = null;
let isNativeKeptAwake = false;

const applyLockState = async () => {
  if (activeLocks.size > 0) {
    // 1. Native platform keep awake (iOS & Android)
    if (Capacitor.isNativePlatform()) {
      try {
        await KeepAwake.keepAwake();
        isNativeKeptAwake = true;
      } catch (nativeErr) {
        console.warn('[ScreenWakeLock] Native KeepAwake.keepAwake failed:', nativeErr);
      }
    }

    // 2. Web Screen Wake Lock API (Desktop Chrome, Edge, Safari 16.4+, Firefox, Mobile browsers)
    if (typeof navigator !== 'undefined' && 'wakeLock' in navigator) {
      if (!webWakeLockSentinel) {
        try {
          webWakeLockSentinel = await navigator.wakeLock.request('screen');
          webWakeLockSentinel.addEventListener('release', () => {
            webWakeLockSentinel = null;
          });
        } catch (webErr) {
          // May fail if tab is not visible or disabled by power policies
          console.warn('[ScreenWakeLock] Web wakeLock request failed:', webErr?.message || webErr);
        }
      }
    }
  } else {
    // Release native keep awake
    if (Capacitor.isNativePlatform() && isNativeKeptAwake) {
      try {
        await KeepAwake.allowSleep();
      } catch (nativeErr) {
        console.warn('[ScreenWakeLock] Native KeepAwake.allowSleep failed:', nativeErr);
      }
      isNativeKeptAwake = false;
    }

    // Release web wake lock
    if (webWakeLockSentinel) {
      try {
        await webWakeLockSentinel.release();
      } catch (webErr) {
        console.warn('[ScreenWakeLock] Web wakeLock release failed:', webErr?.message || webErr);
      }
      webWakeLockSentinel = null;
    }
  }
};

/**
 * Acquire screen wake lock for a given session identifier (e.g. 'live-class', 'ai-tutor')
 * @param {string} key
 */
export const acquireScreenWakeLock = async (key = 'default') => {
  activeLocks.add(key);
  await applyLockState();
};

/**
 * Release screen wake lock for a given session identifier
 * @param {string} key
 */
export const releaseScreenWakeLock = async (key = 'default') => {
  activeLocks.delete(key);
  await applyLockState();
};

/**
 * Release all active screen wake locks unconditionally
 */
export const releaseAllScreenWakeLocks = async () => {
  activeLocks.clear();
  await applyLockState();
};

// Re-acquire lock when user returns to the app / tab becomes visible
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && activeLocks.size > 0) {
      applyLockState();
    }
  });
}

// Re-acquire on native app foregrounding
if (typeof window !== 'undefined' && Capacitor.isNativePlatform()) {
  try {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive && activeLocks.size > 0) {
        applyLockState();
      }
    });
  } catch (_) {}
}

// Cleanup on window unload or pagehide
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (activeLocks.size > 0) {
      releaseAllScreenWakeLocks();
    }
  });
}
